import React, { useState, useEffect } from "react";
import { Joyride, STATUS } from "react-joyride";
import { Box, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";

// ─── Small helpers ─────────────────────────────────────────────────────────────
const titleSx = { fontSize: "14px", fontWeight: 700, mb: 0.6, fontFamily: "'DM Sans', sans-serif", color: "#111" };
const bodySx  = { fontSize: "13px", color: "#555", lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif" };

const StepIcon = ({ children }) => (
  <Box sx={{
    width: 30, height: 30, borderRadius: "9px", bgcolor: "rgba(13,148,136,0.09)",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#0d9488", mb: 1.25,
  }}>
    {children}
  </Box>
);

const DONE_STEP = 8;

// ─── Tour step definitions ─────────────────────────────────────────────────────
const steps = [
  // 0 — Welcome
  {
    target: "body",
    placement: "center",
    disableBeacon: true,
    content: (
      <Box sx={{ textAlign: "center", py: 0.5 }}>
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.05 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "16px", bgcolor: "rgba(13,148,136,0.1)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
            </svg>
          </Box>
        </motion.div>
        <Typography sx={{ fontSize: "21px", fontWeight: 800, mb: 1, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>
          Welcome to Constituency Profiles
        </Typography>
        <Typography sx={{ fontSize: "13.5px", color: "#666", lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif" }}>
          Explore public opinion data across every constituency and ward in Great Britain. This quick tour shows you how to find the issues and audiences that matter most.
        </Typography>
      </Box>
    ),
  },
  // 1 — Variable selector
  {
    target: "#tour-variable-selector",
    placement: "left",
    disableBeacon: true,
    content: (
      <Box>
        <StepIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></StepIcon>
        <Typography sx={titleSx}>Start with a survey question</Typography>
        <Typography sx={bodySx}>Pick any issue - economy, NHS, housing, and more. The map immediately recolours to show how strongly each constituency supports or opposes that issue.</Typography>
      </Box>
    ),
  },
  // 2 — Geography level
  {
    target: "#tour-geo-level",
    placement: "bottom",
    disableBeacon: true,
    content: (
      <Box>
        <StepIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/></svg></StepIcon>
        <Typography sx={titleSx}>Drill down to ward level</Typography>
        <Typography sx={bodySx}>The map starts at <strong>Constituency</strong> level. Once you've selected a constituency, you can switch to <strong>Ward</strong> view to see opinion at a finer grain within it.</Typography>
      </Box>
    ),
  },
  // 3 — Area search
  {
    target: "#tour-area-search",
    placement: "bottom",
    disableBeacon: true,
    content: (
      <Box>
        <StepIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg></StepIcon>
        <Typography sx={titleSx}>Select an area to explore</Typography>
        <Typography sx={bodySx}>Search by name here, or click any shaded area on the map. The sidebar updates instantly with detailed results for that constituency.</Typography>
      </Box>
    ),
  },
  // 4 — Compare chart
  {
    target: "#tour-compare-chart",
    placement: "left",
    disableBeacon: true,
    content: (
      <Box>
        <StepIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg></StepIcon>
        <Typography sx={titleSx}>See how it compares</Typography>
        <Typography sx={bodySx}>Once you select a constituency, this chart benchmarks it against similar areas and the national picture — instantly revealing what makes that place distinctive.</Typography>
      </Box>
    ),
  },
  // 5 — Demographics
  {
    target: "#tour-demographics",
    placement: "right",
    disableBeacon: true,
    content: (
      <Box>
        <StepIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg></StepIcon>
        <Typography sx={titleSx}>Dig into specific audiences</Typography>
        <Typography sx={bodySx}>Filter by demographic group - e.g. <em>Women aged 18–34</em> or <em>Conservative voters</em>. The map and comparison chart both update to reflect that group's views.</Typography>
      </Box>
    ),
  },
  // 6 — Wave selection
  {
    target: "#tour-wave-select",
    placement: "top",
    disableBeacon: true,
    content: (
      <Box>
        <StepIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg></StepIcon>
        <Typography sx={titleSx}>Track opinion over time</Typography>
        <Typography sx={bodySx}>Switch between survey waves to see how public opinion on any issue has shifted — useful for spotting momentum or identifying where attitudes are changing fastest.</Typography>
      </Box>
    ),
  },
  // 7 — Report tab highlight (stays on explore page)
  {
    target: "#tour-report-tab",
    placement: "bottom",
    disableBeacon: true,
    content: (
      <Box>
        <StepIcon><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></StepIcon>
        <Typography sx={titleSx}>Generate reports</Typography>
        <Typography sx={bodySx}>The <strong>Report</strong> tab lets you generate a shareable summary for any constituency — pulling together the key data into a clean, presentation-ready document.</Typography>
      </Box>
    ),
  },
  // 10 — Done (targets body — navigates back to explore while shown)
  {
    target: "body",
    placement: "center",
    disableBeacon: true,
    content: (
      <Box sx={{ textAlign: "center", py: 0.5 }}>
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.05 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "16px", bgcolor: "rgba(22,163,74,0.1)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </Box>
        </motion.div>
        <Typography sx={{ fontSize: "21px", fontWeight: 800, mb: 1, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>
          You're ready to explore
        </Typography>
        <Typography sx={{ fontSize: "13.5px", color: "#666", lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif" }}>
          Pick a question, select a constituency, and start uncovering the story behind the numbers. You can relaunch this tour any time from the <strong>ⓘ</strong> button in the header.
        </Typography>
      </Box>
    ),
  },
];

// ─── Animated tooltip ──────────────────────────────────────────────────────────
const TourTooltip = ({
  continuous, index, step,
  backProps, primaryProps, skipProps, tooltipProps,
}) => (
  <motion.div
    {...tooltipProps}
    initial={{ y: 10, scale: 0.97 }}
    animate={{ y: 0, scale: 1 }}
    exit={{ y: -6, scale: 0.97 }}
    transition={{ type: "spring", stiffness: 340, damping: 28 }}
    style={{ maxWidth: 340 }}
  >
    <Box sx={{
      background: "#fff", borderRadius: "14px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)",
      p: 2.5, border: "1px solid rgba(0,0,0,0.06)", fontFamily: "'DM Sans', sans-serif",
    }}>
      {step.content}

      {/* Progress dots */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.6, mt: 2, mb: 1.5 }}>
        {steps.map((_, i) => (
          <motion.div
            key={i}
            animate={{ width: i === index ? 18 : 6, opacity: i === index ? 1 : 0.3 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            style={{ height: 6, borderRadius: 3, background: "#2dd4bf" }}
          />
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button
          {...skipProps}
          size="small"
          sx={{ fontSize: "11px", color: "#aaa", textTransform: "none", fontFamily: "'DM Sans', sans-serif", minWidth: 0, p: 0 }}
        >
          Skip tour
        </Button>

        <Box sx={{ display: "flex", gap: 0.75 }}>
          {index > 0 && (
            <Button
              {...backProps}
              size="small"
              component={motion.button}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              sx={{
                fontSize: "12px", textTransform: "none", fontFamily: "'DM Sans', sans-serif",
                border: "1px solid rgba(0,0,0,0.15)", color: "#444", borderRadius: "8px",
                px: 1.5, py: 0.45, minWidth: 0, bgcolor: "transparent",
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              Back
            </Button>
          )}
          <Button
            {...primaryProps}
            size="small"
            component={motion.button}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            disableElevation
            sx={{
              fontSize: "12px", textTransform: "none", fontFamily: "'DM Sans', sans-serif",
              bgcolor: "#0d9488", color: "#fff", borderRadius: "8px",
              px: 2, py: 0.45, minWidth: 0,
              "&:hover": { bgcolor: "#0f766e" },
            }}
          >
            {continuous ? (index === steps.length - 1 ? "Done ✓" : "Next →") : "Got it"}
          </Button>
        </Box>
      </Box>
    </Box>
  </motion.div>
);

// ─── Main component ────────────────────────────────────────────────────────────
const STORAGE_KEY = "constituency_tour_done";

export default function GuidedTour({ forceRun = 0, onNavigate }) {
  const [run, setRun] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  useEffect(() => {
    if (forceRun > 0) {
      setTourKey((k) => k + 1);
      setRun(true);
      return;
    }
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) {
      const t = setTimeout(() => {
        localStorage.setItem(STORAGE_KEY, "1");
        setRun(true);
      }, 1400);
      return () => clearTimeout(t);
    }
  }, [forceRun]);

  useEffect(() => {
    if (run) document.body.classList.add("tour-active");
    else      document.body.classList.remove("tour-active");
    return () => document.body.classList.remove("tour-active");
  }, [run]);

  useEffect(() => {
    if (!run) return;
    const forceZIndex = () => {
      const floater = document.querySelector(".react-joyride__floater");
      if (floater) floater.style.setProperty("z-index", "100001", "important");
    };
    const observer = new MutationObserver(forceZIndex);
    observer.observe(document.body, { childList: true, subtree: true });
    forceZIndex();
    return () => observer.disconnect();
  }, [run]);

  const handleCallback = ({ status }) => {
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem(STORAGE_KEY, "1");
      setRun(false);
    }
  };

  return (
    <Joyride
      key={tourKey}
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      showSkipButton
      disableScrollParentFix
      beaconComponent={() => null}
      tooltipComponent={TourTooltip}
      callback={handleCallback}
      floaterProps={{ disableAnimation: false, zIndex: 100010 }}
      styles={{
        options: { zIndex: 100000, overlayColor: "rgba(15, 23, 42, 0.55)", arrowColor: "#fff" },
        spotlight: { borderRadius: 12 },
      }}
    />
  );
}

export const resetTour = () => localStorage.removeItem(STORAGE_KEY);
